import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ============================================================
 * SHARED MIXINS
 * ============================================================ */
export const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

export const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

export const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 0 2em;
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(236, 72, 153, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ============================================================
   * HEADER — padrão unificado (espelha Certificados / XML Scan)
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: attr(data-eyebrow);
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow || tokens.brandRing};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * TABS HEADER
   * ============================================================ */
  .tabs-header {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;

      &:hover { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }

      svg { font-size: 0.95em; }
    }
  }

  /* ============================================================
   * KPI CARDS
   * ============================================================ */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        p:first-child {
          ${mono};
          font-size: 1.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    &.success .card-content .text p:first-child { color: ${tokens.brand}; }
    &.warning .card-content .text p:first-child { color: ${tokens.warn}; }
    &.danger  .card-content .text p:first-child { color: ${tokens.danger}; }
    &.info    .card-content .text p:first-child { color: ${tokens.info}; }

    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.warning .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
    &.danger  .card-content .thumb { color: ${tokens.danger}; opacity: 0.65; }
    &.info    .card-content .thumb { color: ${tokens.info}; opacity: 0.65; }
  }

  /* ============================================================
   * TOOLBAR
   * ============================================================ */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
      flex-wrap: wrap;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 4px;
      ${({ theme }) => cardBase(theme)};
      padding: 3px;
    }

    .btn-new {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${tokens.brand};
      color: white;
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      font-size: 0.78em;
      font-weight: 500;
      letter-spacing: -0.005em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }

      svg { font-size: 0.95em; }
    }

    .btn-secondary {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${({ theme }) => surface(theme, 1)};
      color: ${({ theme }) => theme.text};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      font-size: 0.78em;
      font-weight: 500;
      letter-spacing: -0.005em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover {
        border-color: ${({ theme }) => hairline(theme, true)};
        background: ${({ theme }) => surface(theme, 2)};
      }

      svg { font-size: 0.95em; opacity: 0.75; }
    }

    .btn-danger-outline {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${tokens.dangerSoft};
      color: ${tokens.danger};
      border: 1px solid rgba(239, 68, 68, 0.3);
      border-radius: ${tokens.radiusMd};
      font-size: 0.78em;
      font-weight: 500;
      letter-spacing: -0.005em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover {
        background: rgba(239, 68, 68, 0.18);
      }

      svg { font-size: 0.95em; }
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 360px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      svg {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.9em;
        flex-shrink: 0;
      }

      input {
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        flex: 1;
        letter-spacing: -0.005em;
        min-width: 0;

        &::placeholder { opacity: 0.4; }
      }
    }

    .filter-pill {
      ${mono};
      padding: 6px 12px;
      background: transparent;
      border: none;
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      font-size: 0.66em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover { opacity: 0.9; }

      &.active {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        opacity: 1;
      }
    }
  }

  /* ============================================================
   * LIST SELECTOR BAR (pills de listas de contatos)
   * ============================================================ */
  .list-selector {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .list-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusPill};
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      .count {
        ${mono};
        font-size: 0.85em;
        opacity: 0.6;
      }

      &:hover {
        opacity: 1;
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};

        .count { opacity: 0.85; }
      }

      .list-pill-delete {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        margin-right: -6px;
        margin-left: 2px;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 50%;
        color: inherit;
        font-size: 0.95em;
        cursor: pointer;
        opacity: 0;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg { font-size: 1em; }

        &:hover {
          opacity: 1 !important;
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
        }
      }

      &:hover .list-pill-delete,
      &.active .list-pill-delete {
        opacity: 0.55;
      }
    }

    .list-pill-new {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px dashed ${({ theme }) => hairline(theme, true)};
      border-radius: ${tokens.radiusPill};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover {
        opacity: 1;
        border-color: ${tokens.brand};
        color: ${tokens.brand};
      }
    }
  }

  /* ============================================================
   * BANNER (alerta de campanhas com falha etc.)
   * ============================================================ */
  .info-banner {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 16px;
    margin-bottom: 14px;
    background: ${tokens.dangerSoft};
    border: 1px solid rgba(239, 68, 68, 0.22);
    border-radius: ${tokens.radiusLg};
    flex-shrink: 0;
    animation: ${fadeInOnly} ${tokens.durBase} ${tokens.easeOut};

    .banner-icon {
      width: 36px;
      height: 36px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(239, 68, 68, 0.12);
      border: 1px solid rgba(239, 68, 68, 0.22);
      border-radius: ${tokens.radiusMd};
      color: ${tokens.danger};
      flex-shrink: 0;

      svg { font-size: 1em; }
    }

    .banner-text {
      flex: 1;
      min-width: 0;

      h4 {
        margin: 0;
        font-size: 0.85em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.005em;
      }

      p {
        margin: 2px 0 0 0;
        font-size: 0.72em;
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        letter-spacing: -0.005em;
      }
    }
  }

  /* ============================================================
   * MAIN CONTENT (scroll wrapper)
   * ============================================================ */
  .main-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 3px;
    }
  }

  /* ============================================================
   * CAMPAIGN GRID — cards ricos (com métricas embutidas p/ enviadas)
   * ============================================================ */
  .campaign-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    padding-bottom: 24px;

    @media (max-width: 1500px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 620px)  { grid-template-columns: 1fr; }
  }

  .campaign-card {
    ${({ theme }) => cardBase(theme)};
    padding: 18px 18px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      bottom: 10px;
      width: 2px;
      border-radius: 0 2px 2px 0;
      opacity: 0.55;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};
    }

    &.draft::before     { background: ${tokens.neutral}; }
    &.scheduled::before { background: ${tokens.warn}; }
    &.sending::before   { background: ${tokens.brand}; animation: ${pulseDot} 1.4s ${tokens.easeInOut} infinite; }
    &.sent::before      { background: ${tokens.brand}; }
    &.failed::before    { background: ${tokens.danger}; }

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
      &::before { opacity: 1; }
    }

    /* ---------- HEAD: avatar + título (status vira badge no canto) ---------- */
    .campaign-head {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      min-width: 0;
      padding-right: 66px; /* espaço para o status-pill em badge absoluto */

      .campaign-avatar {
        flex-shrink: 0;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-family: ${tokens.fontMono};
        font-size: 0.68em;
        font-weight: 700;
        letter-spacing: 0.02em;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
      }

      .campaign-top {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
        flex: 1;

        .campaign-eyebrow {
          ${eyebrow};
          font-size: 0.53em;
          opacity: 0.45;
          letter-spacing: 0.05em;
        }

        h5 {
          margin: 0;
          font-size: 0.92em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .campaign-subject {
          margin: 0;
          font-size: 0.72em;
          font-style: italic;
          color: ${({ theme }) => theme.text};
          opacity: 0.55;
          letter-spacing: -0.005em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .status-pill {
      ${mono};
      position: absolute;
      top: 16px;
      right: 16px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 8px;
      border-radius: ${tokens.radiusPill};
      font-size: 0.58em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border: 1px solid transparent;
      white-space: nowrap;
      flex-shrink: 0;

      svg { font-size: 0.95em; }

      &.draft {
        background: ${({ theme }) => surface(theme, 2)};
        color: ${tokens.neutral};
        border-color: ${({ theme }) => hairline(theme, true)};
      }
      &.scheduled {
        background: ${tokens.warnSoft};
        color: ${tokens.warn};
        border-color: rgba(245, 158, 11, 0.22);
      }
      &.sending, &.sent {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};
      }
      &.failed {
        background: ${tokens.dangerSoft};
        color: ${tokens.danger};
        border-color: rgba(239, 68, 68, 0.22);
      }
    }

    /* ---------- TAGS ROW: lista + destinatários ---------- */
    .campaign-tags-row {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .campaign-list-tag {
        display: inline-flex;
        align-items: center;
        padding: 2px 9px;
        border-radius: ${tokens.radiusPill};
        font-family: ${tokens.fontSans};
        font-size: 0.64em;
        font-weight: 700;
        letter-spacing: 0.01em;
        line-height: 1.5;
        color: #fff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      .campaign-recipients {
        ${mono};
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.66em;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        letter-spacing: 0.01em;
        min-width: 0;
        white-space: nowrap;

        svg { font-size: 0.95em; opacity: 0.7; flex-shrink: 0; }
      }
    }

    /* ---------- METRICS: pilha vertical de mini barras (só p/ enviadas) ---------- */
    .campaign-metrics {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px 14px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
    }

    .metric-block {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;

      .metric-label-row {
        display: flex;
        align-items: baseline;
        justify-content: space-between;

        span:first-child {
          ${mono};
          font-size: 0.6em;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }

        span:last-child {
          ${mono};
          font-size: 0.78em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .metric-track {
        height: 5px;
        border-radius: ${tokens.radiusPill};
        background: ${({ theme }) => surface(theme, 2)};
        overflow: hidden;

        .metric-fill {
          height: 100%;
          border-radius: ${tokens.radiusPill};
          transition: width ${tokens.durSlow} ${tokens.easeOut};
        }
      }

      &.open .metric-fill { background: ${tokens.brand}; }
      &.open span:last-child { color: ${tokens.brand}; }
      &.click .metric-fill { background: ${tokens.info}; }
      &.click span:last-child { color: ${tokens.info}; }
      &.bounce .metric-fill { background: ${tokens.danger}; }
      &.bounce span:last-child { color: ${tokens.danger}; }
    }

    /* ---------- ACTIONS ---------- */
    .campaign-actions-row {
      margin-top: auto;
      padding-top: 12px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      display: flex;
      align-items: stretch;
      gap: 8px;
    }

    .campaign-action-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 8px 12px;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: ${tokens.radiusMd};
      font-family: ${tokens.fontSans};
      font-size: 0.74em;
      font-weight: 500;
      letter-spacing: -0.005em;
      color: ${tokens.brand};
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      flex: 1;
      min-width: 0;

      svg { font-size: 0.95em; }

      &:hover {
        background: rgba(16, 185, 129, 0.18);
        transform: translateY(-1px);
      }

      &:disabled {
        cursor: default;
        opacity: 0.6;

        &:hover { transform: none; }

        svg { animation: ${pulseDot} 1.2s ${tokens.easeInOut} infinite; }
      }

      &.secondary {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme)};
        color: ${({ theme }) => theme.text};
        opacity: 0.85;

        &:hover {
          border-color: ${tokens.brandRing};
          color: ${tokens.brand};
          background: ${({ theme }) => surface(theme, 2)};
        }
      }

      &.danger {
        background: ${tokens.dangerSoft};
        border-color: rgba(239, 68, 68, 0.3);
        color: ${tokens.danger};

        &:hover { background: rgba(239, 68, 68, 0.18); }
      }
    }

    .campaign-delete-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 34px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        opacity: 1;
        background: ${tokens.dangerSoft};
        border-color: rgba(239, 68, 68, 0.3);
        color: ${tokens.danger};
      }

      &:disabled { cursor: not-allowed; opacity: 0.25; }
    }
  }

  /* ============================================================
   * CONTACTS TABLE
   * ============================================================ */
  .contacts-table {
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    padding-bottom: 24px;
    margin-bottom: 24px;

    .table-header {
      display: grid;
      grid-template-columns: 28px 1.4fr 1.6fr 1.4fr 0.8fr 0.9fr 70px;
      gap: 10px;
      padding: 12px 16px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      span {
        ${mono};
        font-size: 0.62em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
      }

      input[type="checkbox"] {
        width: 15px;
        height: 15px;
        accent-color: ${tokens.brand};
        cursor: pointer;
      }
    }

    .table-body {
      .table-row {
        display: grid;
        grid-template-columns: 28px 1.4fr 1.6fr 1.4fr 0.8fr 0.9fr 70px;
        gap: 10px;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid ${({ theme }) => hairline(theme)};
        transition: background ${tokens.durBase} ${tokens.easeOut};

        &:last-child { border-bottom: none; }

        &:hover { background: ${({ theme }) => theme.tableRowHover}; }

        input[type="checkbox"] {
          width: 15px;
          height: 15px;
          accent-color: ${tokens.brand};
          cursor: pointer;
        }

        .cell-name {
          font-size: 0.82em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.005em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .cell-email {
          ${mono};
          font-size: 0.76em;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .cell-tags {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          min-width: 0;

          .tag {
            display: inline-flex;
            align-items: center;
            padding: 2px 8px;
            border-radius: ${tokens.radiusPill};
            font-size: 0.62em;
            font-weight: 700;
            color: #fff;
            white-space: nowrap;
          }
        }

        .cell-status {
          ${mono};
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: ${tokens.radiusPill};
          font-size: 0.6em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          border: 1px solid transparent;
          white-space: nowrap;
          justify-self: start;

          &.active {
            background: ${tokens.brandSoft};
            color: ${tokens.brand};
            border-color: ${tokens.brandRing};
          }
          &.removed {
            background: ${({ theme }) => surface(theme, 2)};
            color: ${tokens.neutral};
            border-color: ${({ theme }) => hairline(theme, true)};
          }
        }

        .cell-date {
          ${mono};
          font-size: 0.72em;
          color: ${({ theme }) => theme.text};
          opacity: 0.55;
        }

        .cell-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 4px;

          button {
            width: 26px;
            height: 26px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: 1px solid ${({ theme }) => hairline(theme)};
            border-radius: ${tokens.radiusSm};
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
            cursor: pointer;
            transition: all ${tokens.durBase} ${tokens.easeOut};

            svg { font-size: 0.82em; }

            &:hover {
              opacity: 1;
              background: ${tokens.dangerSoft};
              border-color: rgba(239, 68, 68, 0.3);
              color: ${tokens.danger};
            }
          }
        }
      }
    }
  }

  /* ============================================================
   * EMPTY STATE
   * ============================================================ */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    background: transparent;
    border: none;
    text-align: center;

    .empty-icon {
      width: 64px;
      height: 64px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};
      opacity: 0.7;

      svg { font-size: 1.6em; }
    }

    h4 {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 300px;
      line-height: 1.55;
    }

    .empty-cta {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 9px 18px;
      background: ${tokens.brand};
      color: white;
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      font-size: 0.8em;
      font-weight: 500;
      cursor: pointer;
      margin-top: 8px;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }

      svg { font-size: 1em; }
    }
  }
`;

/* ============================================================
 * TAG PALETTE — cor consistente por texto (mesmo padrão de
 * certificates/index.js)
 * ============================================================ */
export const TAG_PALETTE = [
  "#dc2626", "#ea580c", "#65a30d", "#16a34a", "#0d9488",
  "#0891b2", "#2563eb", "#4f46e5", "#9333ea", "#db2777",
];

export const getColorFromText = (text) => {
  const seed = (text || "").toString().trim().toLowerCase();
  if (!seed) return TAG_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
};
